import {type LngLat, type LngLatBounds, lngLatsToBounds} from '@yandex-delivery/blocks'
import {
  applySpec,
  defaultTo,
  firstOf,
  flow,
  get,
  identity,
  ifElse,
  makeGet,
  map,
  pipe,
  stubTrue,
} from '@yandex-delivery/utils'

import {i18n} from '../../../../config'
import type {PickupStation} from '../../../../types'
import {formatSchedule, isValidCoordinates} from '../../../../utils'
import type {fetchPickUpDropOffPoints} from '../../../deprecated/fetch-pick-up-drop-off-points'
import type {
  NearestRoute,
  PickupPoint,
  PickupPointCity,
  PickupPointFeature,
  PickupPointID,
  PickupPoints,
  PickupPointsWithBounds,
  PickupPointsWithFeatures,
  PickupPointsWithNearest,
  PickupPointType,
} from '../../../models/pickup-route'

export type PickupDropoffRequest = Parameters<typeof fetchPickUpDropOffPoints>[0]
export type PickupDropoffResponse = Awaited<ReturnType<typeof fetchPickUpDropOffPoints>>
export const toPickupDropoffRequest: (city: PickupPointCity) => PickupDropoffRequest = applySpec<PickupDropoffRequest>({
  geo_id: get('id'),
  is_not_branded_partner_station: stubTrue,
})
const raise = (msg: string) => (v?: unknown) => {
  throw new Error(msg + v ? ` ${String(v)}` : '')
}
const pickupStationGet = makeGet<PickupStation>()

function toPickupPointType(pickupStation: PickupStation): PickupPointType {
  // @ts-expect-error свойство "is_market_partner" есть в ответе бекенда, наверно типы устарели
  if (pickupStation.is_market_partner === true) {
    return 'yandex_market'
  }

  return 'pickup_point'
}

const toCoordinates: (pickupStation: PickupStation) => LngLat = flow(
  applySpec<number[]>([pickupStationGet('position.longitude'), pickupStationGet('position.latitude')]),
  ifElse(
    isValidCoordinates, //
    identity as (coordinates: number[]) => LngLat,
    raise('[toCoordinates] longitude/longitude invalid'),
  ),
)

function toPickupPoint(pickupStation: PickupStation): PickupPoint {
  return {
    _id: pickupStation.id as PickupPointID,
    _type: toPickupPointType(pickupStation),
    coordinates: toCoordinates(pickupStation),
    name: pipe(
      pickupStation,
      firstOf(
        get('address.full_address'), //
        get('name'),
      ),
      defaultTo(''),
    ),
    schedule: pipe(
      pickupStation, //
      get('schedule'),
      schedule => formatSchedule(schedule, i18n),
    ),
  }
}

function toPickupPointFeature(pickupStation: PickupStation): PickupPointFeature {
  return {
    id: pickupStationGet('id', pickupStation),
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: toCoordinates(pickupStation),
    },
    pickupPoint: toPickupPoint(pickupStation),
  }
}

export function toPickupPoints(source?: LngLat) {
  return function (response?: PickupDropoffResponse): PickupPoints {
    return pipe(
      response, //
      withFeatures,
      source ? withNearest(source) : withBounds,
    )
  }
}

const withFeatures: (response?: PickupDropoffResponse) => PickupPointsWithFeatures =
  applySpec<PickupPointsWithFeatures>({
    features: map(toPickupPointFeature),
  })

function withBounds(model: PickupPointsWithFeatures): PickupPointsWithFeatures | PickupPointsWithBounds {
  const {features} = model

  if (features.length > 0) {
    return {
      features,
      bounds: pipe(
        features,
        map(get('geometry.coordinates')),
        lngLatsToBounds,
        (bounds?: LngLatBounds): LngLatBounds => {
          return bounds || raise('[withBounds] bounds are empty')()
        },
      ),
    }
  }

  return model
}

function withNearest(source: LngLat) {
  return function _withNearest(model: PickupPointsWithFeatures): PickupPointsWithFeatures | PickupPointsWithNearest {
    const {features} = model

    if (features.length > 0) {
      const [x1, y1] = source
      // ищем ближайший ПВЗ от пользователя
      const {feature} = features.reduce<{distance: number; feature: PickupPointFeature}>(
        (acc, featureItem) => {
          const [x2, y2] = featureItem.geometry.coordinates
          // расстояние между двумя точками
          const distance = Math.sqrt((x1 - x2) ** 2 + (y1 - y2) ** 2)

          if (distance < acc.distance) {
            return {
              distance,
              feature: featureItem,
            }
          }

          return acc
        },
        {distance: Infinity, feature: features[0]},
      )
      const route: NearestRoute = [source, feature.geometry.coordinates]

      return {
        features,
        nearest: {
          feature,
          route,
          bounds: lngLatsToBounds(route, true) || raise('[withNearestPoint] cant compute bounds')(),
        },
      }
    }

    return model
  }
}
